import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Alert, CircularProgress,
    Card, CardContent, Stack
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { FaChartBar, FaCalendarAlt } from 'react-icons/fa';
import { getBudgetVariance, getCompetences } from '../api/api';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend
);

const AnaliseOrcamentariaPage = () => {
    const theme = useTheme();
    const [meses, setMeses] = useState([]);
    const [selectedMes, setSelectedMes] = useState('');
    const [varianceData, setVarianceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadMonths();
    }, []);

    useEffect(() => {
        if (selectedMes) {
            loadVariance();
        }
    }, [selectedMes]);

    const loadMonths = async () => {
        try {
            const response = await getCompetences();
            if (response.data && Array.isArray(response.data)) {
                const activeMonths = response.data.map(c => c.mes || c).sort();
                setMeses(activeMonths);

                if (activeMonths.length > 0) {
                    // Set most recent as default
                    setSelectedMes(activeMonths[activeMonths.length - 1]);
                }
            } else {
                setMeses([]);
            }
        } catch (err) {
            console.error("Erro ao carregar meses:", err);
        }
    };

    const loadVariance = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getBudgetVariance(selectedMes);
            setVarianceData(response.data);
        } catch (err) {
            setError('Erro ao carregar análise de variância.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
    };

    return (
        <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }} className="page-fade-in">
            <Typography variant="h3" fontWeight="bold" gutterBottom className="premium-gradient-text" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <FaChartBar /> Análise Orçamentária
            </Typography>

            <Paper sx={{ p: 2, mb: 4, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }} className="glassmorphism">
                <FaCalendarAlt color={theme.palette.primary.main} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 2 }}>Competência:</Typography>
                <TextField
                    select
                    value={selectedMes}
                    onChange={(e) => setSelectedMes(e.target.value)}
                    SelectProps={{ native: true }}
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                >
                    <option value="" disabled>Selecione</option>
                    {meses.map(m => <option key={m} value={m}>{m}</option>)}
                </TextField>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? <CircularProgress /> : (
                <Stack spacing={3}>
                    {/* Charts Section */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">Top 10 Variações Absolutas (R$)</Typography>
                                <Box sx={{ height: 300 }}>
                                    <Bar
                                        data={{
                                            labels: varianceData
                                                .sort((a, b) => Math.abs(b.variacao_valor) - Math.abs(a.variacao_valor))
                                                .slice(0, 10)
                                                .map(v => `${v.centro_custo} - ${v.conta}`),
                                            datasets: [
                                                {
                                                    label: 'Variação (R$)',
                                                    data: varianceData
                                                        .sort((a, b) => Math.abs(b.variacao_valor) - Math.abs(a.variacao_valor))
                                                        .slice(0, 10)
                                                        .map(v => v.variacao_valor),
                                                    backgroundColor: varianceData
                                                        .sort((a, b) => Math.abs(b.variacao_valor) - Math.abs(a.variacao_valor))
                                                        .slice(0, 10)
                                                        .map(v => v.variacao_valor > 0 ? 'rgba(211, 47, 47, 0.7)' : 'rgba(56, 142, 60, 0.7)'),
                                                }
                                            ]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                            }
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card className="glassmorphism sach-card" sx={{ borderRadius: 4 }}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                                        <FaChartBar color={theme.palette.secondary.main} />
                                        <Typography variant="subtitle1" fontWeight="bold">Resumo por Severidade</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 2, borderRadius: 3, background: alpha(theme.palette.error.main, 0.1), border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.2) }}>
                                                <Typography variant="h3" fontWeight="bold" color="error.main">{varianceData.filter(v => Math.abs(v.variacao_percentual) > 20).length}</Typography>
                                                <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'error.main' }}>Críticas ({'>'}20%)</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 2, borderRadius: 3, background: alpha(theme.palette.warning.main, 0.1), border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.2) }}>
                                                <Typography variant="h3" fontWeight="bold" color="warning.dark">{varianceData.filter(v => Math.abs(v.variacao_percentual) > 10 && Math.abs(v.variacao_percentual) <= 20).length}</Typography>
                                                <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', color: 'warning.dark' }}>Atenção (10-20%)</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Paper sx={{ p: 0, borderRadius: 4, overflow: 'hidden' }} className="glassmorphism">
                        <Box p={3} bgcolor="rgba(0,0,0,0.02)" borderBottom="1px solid var(--border-color)">
                            <Typography variant="h6" fontWeight="bold">Detalhamento de Variância</Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow sx={{ '& th': { bgcolor: 'rgba(0,0,0,0.03)', fontWeight: 'bold' } }}>
                                        <TableCell>Centro de Custo</TableCell>
                                        <TableCell>Conta</TableCell>
                                        <TableCell align="right">Realizado</TableCell>
                                        <TableCell align="right">Orçado</TableCell>
                                        <TableCell align="right">Variação (R$)</TableCell>
                                        <TableCell align="right">Variação (%)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {varianceData.map((row, index) => {
                                        const absPct = Math.abs(row.variacao_percentual);
                                        let severityColor = 'inherit';
                                        let severityBg = 'transparent';

                                        if (absPct > 20) {
                                            severityColor = theme.palette.error.main;
                                            severityBg = alpha(theme.palette.error.main, 0.05);
                                        } else if (absPct > 10) {
                                            severityColor = theme.palette.warning.dark;
                                            severityBg = alpha(theme.palette.warning.main, 0.05);
                                        }

                                        return (
                                            <TableRow key={index} hover sx={{ bgcolor: severityBg }}>
                                                <TableCell sx={{ fontWeight: 500 }}>{row.centro_custo}</TableCell>
                                                <TableCell>{row.conta}</TableCell>
                                                <TableCell align="right">{formatCurrency(row.real)}</TableCell>
                                                <TableCell align="right">{formatCurrency(row.orcado)}</TableCell>
                                                <TableCell align="right" sx={{ color: row.variacao_valor > 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                                                    {formatCurrency(row.variacao_valor)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Box sx={{
                                                        display: 'inline-block',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 1.5,
                                                        bgcolor: severityBg === 'transparent' ? 'action.hover' : severityBg,
                                                        color: severityColor === 'inherit' ? 'text.primary' : severityColor,
                                                        fontWeight: 'bold',
                                                        fontSize: '0.85rem',
                                                        border: severityColor !== 'inherit' ? `1px solid ${alpha(severityColor, 0.2)}` : 'none'
                                                    }}>
                                                        {row.variacao_percentual.toFixed(2)}%
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Stack>
            )}
        </Box>
    );
};

export default AnaliseOrcamentariaPage;
